import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-daisyui';
import { Link } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import * as z from 'zod';
import { useAuthContext } from '@/context/AuthContext.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';

const INPUT_LABEL_CLASS =
  '!bg-[#FAFAFA] !border !border-[rgba(204,204,204,0.5)] !rounded-xl !min-h-[48px] font-dm text-sm text-#1F2937';

const loginSchema = z.object({
  email: z.string().min(1, { message: 'This field is required.' }).email('Invalid email'),
  password: z.string().min(1, { message: 'This field is required.' }),
});

type LoginSchema = z.infer<typeof loginSchema>;

const LoginPage: FC = () => {
  const { control, handleSubmit } = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });
  const { t } = useTranslation();
  const {
    loginQuery: { mutateAsync: login, isPending: isLoadingLogin, error: errorLogin, isError: isErrorLogin },
  } = useAuthContext();

  const handleLogin = (data: LoginSchema) => {
    void (async () => {
      try {
        if (isLoadingLogin) return;
        await login({ email: data.email, password: data.password });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <BrandCardSideContent centerContent>
      <form className="flex flex-col gap-4 w-full" onSubmit={handleSubmit(handleLogin)}>
        <div className="flex flex-col gap-2 py-6">
          <h1 className="font-newsreader font-bold text-2xl leading-[117%] text-[rgba(0,0,0,0.87)]">
            {t('loginPage.title')}
          </h1>
          <p className="font-dm text-sm font-normal leading-[150%] tracking-[0.15px] text-[rgba(0,0,0,0.6)]">
            {t('loginPage.description')}
          </p>
        </div>

        {isErrorLogin && <Alert status="error">{errorLogin?.message}</Alert>}

        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, value }, formState: { errors } }) => (
            <TextInput
              error={errors.email?.message}
              value={value}
              onChange={onChange}
              placeholder={t('label.email')}
              labelClassName={INPUT_LABEL_CLASS}
            />
          )}
        />
        <Controller
          control={control}
          name="password"
          render={({ field: { onChange, value }, formState: { errors } }) => (
            <TextInput
              error={errors.password?.message}
              value={value}
              onChange={onChange}
              type="password"
              placeholder={t('label.password')}
              labelClassName={INPUT_LABEL_CLASS}
            />
          )}
        />

        <LoadingButton loading={isLoadingLogin} type="submit" color="primary" className="w-full">
          {t('button.signIn')}
        </LoadingButton>

        <div className="flex items-center justify-between">
          <Link to={Routes.FORGOT_PASSWORD} className="font-dm font-semibold text-sm text-[#122346]">
            {t('button.forgotPassword')}?
          </Link>
          <Link to={Routes.REGISTER} className="font-dm font-semibold text-sm text-[#122346]">
            {t('button.createAccount')}
          </Link>
        </div>
      </form>
    </BrandCardSideContent>
  );
};

export default LoginPage;
